import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type TenantStatus } from '../lib/api';
import { Button, ErrorText, Field, inputClass, Modal } from './ui';

const DEFAULT_REASON = 'Accesul la Firmio a fost suspendat. Contactați furnizorul pentru detalii.';

/**
 * ON/OFF. Turning a tenant OFF asks for the message its users will see; the
 * instance applies it at its next heartbeat.
 */
export function SuspendToggle({
  tenantId,
  tenantName,
  status,
  compact,
}: {
  tenantId: string;
  tenantName: string;
  status: TenantStatus;
  compact?: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<'off' | 'on' | null>(null);
  const [reason, setReason] = useState(DEFAULT_REASON);

  const mutation = useMutation({
    mutationFn: () =>
      confirming === 'off' ? api.suspend(tenantId, reason) : api.activate(tenantId),
    onSuccess: async () => {
      setConfirming(null);
      await queryClient.invalidateQueries();
    },
  });

  const isOn = status === 'ACTIVE';
  const label = compact ? (isOn ? 'ON' : 'OFF') : isOn ? 'Acces pornit' : 'Acces oprit';
  return (
    <>
      {/* The switch shows the state and changes it; a click always confirms first. */}
      <button
        type="button"
        role="switch"
        aria-checked={isOn}
        aria-label={`Acces ${tenantName}: ${isOn ? 'pornit' : 'oprit'}`}
        title={isOn ? 'Click pentru a opri accesul' : 'Click pentru a porni accesul'}
        onClick={() => setConfirming(isOn ? 'off' : 'on')}
        className="inline-flex items-center gap-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
      >
        <span
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
            isOn ? 'bg-emerald-500' : 'bg-slate-700'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
              isOn ? 'translate-x-[22px]' : 'translate-x-0.5'
            }`}
          />
        </span>
        <span
          className={`whitespace-nowrap text-xs font-semibold ${
            compact ? 'w-7 text-left' : ''
          } ${isOn ? 'text-emerald-300' : 'text-red-300'}`}
        >
          {label}
        </span>
      </button>
      {confirming && (
        <Modal
          title={
            confirming === 'off'
              ? `Oprești accesul pentru ${tenantName}?`
              : `Pornești accesul pentru ${tenantName}?`
          }
          onClose={() => setConfirming(null)}
        >
          <div className="space-y-4">
            {confirming === 'off' ? (
              <>
                <p className="text-sm text-slate-300">
                  La următorul heartbeat, în cel mult câteva minute, nimeni din firmă nu se mai
                  poate loga, inclusiv adminul. Toți vor vedea mesajul de mai jos. Datele nu se
                  șterg.
                </p>
                <Field label="Mesajul afișat utilizatorilor">
                  <textarea
                    rows={3}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className={inputClass}
                  />
                </Field>
              </>
            ) : (
              <p className="text-sm text-slate-300">
                Accesul revine la următorul heartbeat al instanței, în cel mult câteva minute.
              </p>
            )}
            <ErrorText error={mutation.error} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Renunță
              </Button>
              <Button
                variant={confirming === 'off' ? 'danger' : 'primary'}
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {confirming === 'off' ? 'Oprește accesul' : 'Pornește accesul'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}
